import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { ConnectionStats, QueueStatsResponse, QueueDeletionInfo } from '../types';

export function useRabbitMqMonitoring() {
  const httpClient = useHttpClient();
  const [connectionStats, setConnectionStats] = useState<ConnectionStats | null>(null);
  const [queueStats, setQueueStats] = useState<QueueStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConnectionStats = useCallback(async () => {
    try {
      const response = await httpClient.get<ConnectionStats>(
        getApiUrl('system', '/api/rabbitmqmanagement/connection-status')
      );
      setConnectionStats(response.data ?? null);
    } catch (err: any) {
      console.error('Error fetching RabbitMQ connection stats:', err);
      throw err;
    }
  }, [httpClient]);

  const fetchQueueStats = useCallback(async () => {
    try {
      const response = await httpClient.get<QueueStatsResponse>(
        getApiUrl('system', '/api/rabbitmqmanagement/queue-stats')
      );
      setQueueStats(response.data ?? null);
    } catch (err: any) {
      console.error('Error fetching queue stats:', err);
      throw err;
    }
  }, [httpClient]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchConnectionStats(), fetchQueueStats()]);
    } catch (err: any) {
      // Check if it's a 404 error indicating backend not available
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        setError('RabbitMQ management endpoints not available. Please start the local CCMS.Api.System backend service or update the proxy configuration to route /system/* to your local API.');
      } else {
        setError(err.message || 'Failed to fetch RabbitMQ data');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchConnectionStats, fetchQueueStats]);

  const verifyQueueDeletion = useCallback(
    async (queueName: string): Promise<QueueDeletionInfo> => {
      const response = await httpClient.get<QueueDeletionInfo>(
        getApiUrl('system', `/api/rabbitmqmanagement/queue-verification/${queueName}`)
      );
      return (
        response.data ?? { canDelete: false, messageCount: 0, consumerCount: 0, warnings: ['No response from server'] }
      );
    },
    [httpClient]
  );

  const deleteQueue = useCallback(
    async (queueName: string, force: boolean = false) => {
      await httpClient.delete(
        getApiUrl('system', `/api/rabbitmqmanagement/queues/${queueName}?force=${force}`)
      );
    },
    [httpClient]
  );

  const purgeQueue = useCallback(
    async (queueName: string) => {
      await httpClient.post(getApiUrl('system', `/api/rabbitmqmanagement/queues/${queueName}/purge`), {});
    },
    [httpClient]
  );

  return {
    connectionStats,
    queueStats,
    loading,
    error,
    setError,
    fetchAll,
    verifyQueueDeletion,
    deleteQueue,
    purgeQueue,
  };
}
